import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import { usePageRefresh } from '../context/PullToRefreshContext';
import {
  cacheAvailableCalls,
  getCachedAvailableCalls,
  cacheTodaySets,
  getCachedTodaySets,
} from '../utils/scheduleCache';
import { useVisitTask } from '../hooks/useVisitTask';
import { useVisitProjectTimes } from '../hooks/useVisitProjectTimes';
import { deriveSiteTasks } from '../utils/siteTasks';
import { MAX_OPEN_LEG_MINUTES, dayUnassignedMinutes, legMiles, previousClosedLeg } from '../utils/dayLegs';
import { storeAtHand } from '../utils/nearestStore';
import type { TodayStoreRef } from '../utils/nearestStore';
import ClockInLocationFork from '../components/ClockInLocationFork';
import { othersWhoCovered } from '../utils/coverage';
import { getEffectiveAssociateId } from '../utils/associate';
import { formatCompactMinutes } from '../utils/visitProjectTime';
import { useDayTrackingContext } from '../context/DayTrackingContext';
import { formatDateToLocal } from '../utils/timesheetRules';
import { toNaiveLocal } from '../utils/visitTime';
import { useOdometerContext } from '../context/OdometerContext';
import { useSchedule, type ScheduledStore } from '../hooks/useSchedule';
import { setsApi, availableCallsApi, routesApi, projectsApi, type ScheduledSet, type AvailableCall, type RouteSummary, type Project } from '../services/api';
import InstallPrompt from '../components/InstallPrompt';
import PageHeader from '../components/PageHeader';
import OdometerEntry from '../components/OdometerEntry';
import LocationPermission from '../components/LocationPermission';
import TaskCard from '../components/TaskCard';
import { requirementsFromFlags } from '../components/RequirementPills';

// Demo-data fallback removed (#1496). When the schedule API fails, the user
// now sees an explicit "couldn't load schedule" empty state with a retry
// button instead of seeing fake stores from a different city. Their actual
// in-progress visit data remains available via the day-tracking IDB cache.

// Filter the raw getMySets window down to what Today shows: sets dated today,
// plus past-due sets still inside their delivery window (grace period for
// missed sets), sorted by set_date. Extracted so the cache-first offline
// repaint (#3072) applies the exact same rules as the live fetch — always
// against the CURRENT date, so a stale cache can't resurface an expired set.
function filterSetsForToday(sets: ScheduledSet[], todayStr: string): ScheduledSet[] {
  const filtered = sets.filter((set) => {
    // Extract date part directly from ISO string to avoid timezone conversion
    const setDate = set.set_date.split('T')[0];
    const windowEnd = set.display_specs?.window_end?.split('T')[0];

    // 1. Show if specifically assigned for today
    if (setDate === todayStr) return true;

    // 2. Show if past-due but still within window (grace period for missed sets)
    if (setDate < todayStr && windowEnd && todayStr <= windowEnd) return true;

    return false;
  });

  // Sort by set_date (datetime order)
  filtered.sort((a, b) => new Date(a.set_date).getTime() - new Date(b.set_date).getTime());
  return filtered;
}

// Live timer component
const MAX_OPEN_LEG_SECONDS = MAX_OPEN_LEG_MINUTES * 60;

/**
 * Ticking clock for the leg in progress. The elapsed time is clamped: a punch
 * the rep forgot to close is a missing punch, and showing it as 20 hours of
 * driving is what made the timer look runaway (triage #2). Past the clamp the
 * value is marked with `+` so it reads as "at least this", not as a total.
 *
 * `baseSeconds` lets a timer resume from time already banked — a project the
 * rep paused and came back to keeps its running sub-total.
 */
function LiveTimer({
  startTime,
  baseSeconds = 0,
  compact = false,
}: {
  startTime: Date;
  baseSeconds?: number;
  compact?: boolean;
}) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const updateElapsed = () => {
      const now = Date.now();
      setElapsed(Math.floor((now - startTime.getTime()) / 1000));
    };
    updateElapsed();
    const timer = setInterval(updateElapsed, 1000);
    return () => clearInterval(timer);
  }, [startTime]);

  const clamped = Math.min(Math.max(0, elapsed), MAX_OPEN_LEG_SECONDS);
  const total = clamped + Math.max(0, baseSeconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const overrun = elapsed > MAX_OPEN_LEG_SECONDS ? '+' : '';

  if (compact) {
    return (
      <span className="font-mono">
        {hours > 0 ? `${hours}h ${minutes.toString().padStart(2, '0')}m` : `${minutes}m`}{overrun}
      </span>
    );
  }

  return (
    <span className="font-mono">
      {hours.toString().padStart(2, '0')}:{minutes.toString().padStart(2, '0')}:{seconds.toString().padStart(2, '0')}{overrun}
    </span>
  );
}

export default function Today() {
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());

  // Try to fetch schedule from API
  const {
    stores: apiStores,
    isLoading: isScheduleLoading,
    error: scheduleError,
    updateStoreStatus,
    refetch: refetchSchedule,
  } = useSchedule();

  // Bumped by pull-to-refresh to re-run the section fetchers below (#2058).
  const [refreshNonce, setRefreshNonce] = useState(0);

  // Use API stores if available, otherwise fall back to demo data
  const [stores, setStores] = useState<ScheduledStore[]>([]);

  const {
    dayData,
    isLoading: isDayLoading,
    error: dayError,
    clockIn,
    clockOut,
    startDriving,
    stopDriving,
    formatTime,
    getLastCompletedStoreEntry,
    clockWindows,
  } = useDayTrackingContext();

  // Clocked-in time no visit or drive accounts for. Today is where the rep
  // looks; the repair lives on Time — say so instead of letting the hole wait
  // to be discovered at payroll.
  const todayUnaccountedMinutes = useMemo(
    () => dayUnassignedMinutes(clockWindows || [], dayData.timeEntries || []),
    [clockWindows, dayData.timeEntries],
  );

  const {
    odometerData,
    isLoading: isOdometerLoading,
    startDayOdometer,
    endDayOdometer,
    calculateExpectedOdometer,
  } = useOdometerContext();

  const [showOdometerEntry, setShowOdometerEntry] = useState(false);
  const [odometerEntryType, setOdometerEntryType] = useState<'start_day' | 'end_day'>('start_day');
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [hasLocationPermission, setHasLocationPermission] = useState<boolean | null>(null);

  // User's current location for proximity sorting
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [clockInFork, setClockInFork] = useState<{ candidate: TodayStoreRef | null } | null>(null);

  // Haversine formula to calculate distance in km between two coordinates
  const calculateDistance = (lat1: number, lon1: number, lat2: number | null, lon2: number | null): number | null => {
    if (lat2 === null || lon2 === null) return null;
    const R = 6371; // Earth's radius in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Get user's location on mount for proximity sorting. Even for sort
  // purposes we want a real GPS fix - WiFi/cell triangulation can be
  // miles off and would mis-sort the list. #1818
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        () => {
          // Silently fail - proximity sorting won't be available
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
      );
    }
  }, []);


  const {
    isInstallable,
    isIOS,
    isStandalone,
    promptInstall,
    dismissPrompt,
    showIOSInstructions,
    setShowIOSInstructions,
  } = useInstallPrompt();

  // Helper function to construct a proper store display name
  // Handles cases where store_name is "#None" or invalid
  const getStoreDisplayName = (store: {
    store_name: string;
    retailer_abbreviation?: string;
    retailer_name?: string;
    legacy_store_id?: string | null;
    store_number?: string;
  }) => {
    // Check if store_name is valid (not "#None", not empty)
    const trimmedName = store.store_name?.trim() || '';
    const isInvalidName = !trimmedName ||
      trimmedName === '#None' ||
      trimmedName.toLowerCase() === 'none' ||
      trimmedName === '#null' ||
      trimmedName.toLowerCase().includes('#none');

    if (!isInvalidName) {
      return store.store_name;
    }

    // Construct from retailer + store number
    const retailer = store.retailer_abbreviation || store.retailer_name || 'Store';
    const storeNum = store.legacy_store_id || store.store_number || '';

    if (storeNum) {
      return `${retailer} #${storeNum}`;
    }
    return retailer;
  };

  // Today's sets state (sets where today falls within window or setdate)
  const [todaySets, setTodaySets] = useState<ScheduledSet[]>([]);
  const [setsLoading, setSetsLoading] = useState(true);
  const [overdueCollapsed, setOverdueCollapsed] = useState(true); // Overdue section starts collapsed

  // Completed sets today (for header stats)
  const [completedSetsToday, setCompletedSetsToday] = useState(0);

  // Calls logged today (header-stat fallback when route summary is absent).
  // Derived from the already-loaded schedule stores — no extra getCallLogs
  // fetch (#2058 follow-up). Counts completed store visits today. The old
  // separate fetch also compared MM/DD/YYYY dtecall to a YYYY-MM-DD string, so
  // it always returned 0; deriving from stores fixes that too.
  const callsLoggedToday = useMemo(
    () => stores.filter((s) => s.status === 'completed').length,
    [stores]
  );

  // Split sets into today's scheduled vs overdue (past-due but in window)
  // Then sort by proximity to user's current location
  const todayStr = formatDateToLocal(new Date());

  // Site tasks for the active store (#1955/#1995). The store visit is a
  // wrapper holding one task per project; surface the active one + the rest
  // on the banner. Sourced from the authoritative useVisitTask state
  // (currentTask/pausedTasks/completedTasks) merged with the active store's
  // scheduled projects (todo), so statuses + names are correct and refresh
  // on transitions. Each task carries the site key (legacyStoreId +
  // retailerId) + projectId; ScheduledStore doesn't expose retailerId yet,
  // so it's null on Today until the store models carry it.
  const { visitState } = useVisitTask();
  const activeSiteStore = stores.find((s) => s.id === dayData.currentStoreId);
  const siteTasks = useMemo(
    () =>
      deriveSiteTasks({
        legacyStoreId: dayData.currentStoreId ?? 0,
        retailerId: null,
        visitState,
        scheduledProjects: activeSiteStore
          ? activeSiteStore.projectIds.map((id, i) => ({ id, name: activeSiteStore.projects[i] ?? `#${id}` }))
          : [],
      }),
    [dayData.currentStoreId, visitState, activeSiteStore],
  );
  const activeSiteTask = siteTasks.find((t) => t.status === 'active') ?? null;

  // Who "I" am, so a coverage badge never names the rep back to themselves.
  const myAssociateId = useMemo(() => getEffectiveAssociateId(), []);

  // Contextual timer inputs (#3452). The open leg says what the rep is doing;
  // the leg before it is what they just finished, surfaced as a glance so
  // leaving a store never needs a modal.
  const openStoreLeg = useMemo(
    () => dayData.timeEntries.find((e) => e.type === 'store' && !e.endTime) ?? null,
    [dayData.timeEntries],
  );
  const openDriveLeg = useMemo(
    () => dayData.timeEntries.find((e) => e.type === 'drive' && !e.endTime) ?? null,
    [dayData.timeEntries],
  );
  const driveToHere = useMemo(
    () => previousClosedLeg(dayData.timeEntries, openStoreLeg, 'drive'),
    [dayData.timeEntries, openStoreLeg],
  );
  const storeJustLeft = useMemo(
    () => previousClosedLeg(dayData.timeEntries, openDriveLeg, 'store'),
    [dayData.timeEntries, openDriveLeg],
  );
  // Re-read the sub-totals when the visit's task state actually changes, not on
  // every render that hands back a new visitState object.
  const visitTaskKey = useMemo(
    () =>
      JSON.stringify({
        active: visitState.currentTask?.project_id ?? null,
        paused: visitState.pausedTasks.map((p) => p.uuid),
        done: visitState.completedTasks.map((t) => t.project_id),
      }),
    [visitState],
  );
  const projectTimes = useVisitProjectTimes(openStoreLeg?.startTime ?? null, visitTaskKey);

  // Helper to get distance for sorting (returns Infinity if no location data)
  const getDistanceForSort = (lat: number | null, lon: number | null): number => {
    if (!userLocation || lat === null || lon === null) return Infinity;
    return calculateDistance(userLocation.lat, userLocation.lng, lat, lon) || Infinity;
  };

  const todayScheduledSets = todaySets
    .filter((set) => {
      // Extract date part directly from ISO string to avoid timezone conversion
      const setDateStr = set.set_date.split('T')[0];
      return setDateStr >= todayStr; // Today or future
    })
    .sort((a, b) => {
      // Sort by distance (closest first)
      const distA = getDistanceForSort(a.store.lat, a.store.lon);
      const distB = getDistanceForSort(b.store.lat, b.store.lon);
      return distA - distB;
    });

  const overdueSets = todaySets
    .filter((set) => {
      // Extract date part directly from ISO string to avoid timezone conversion
      const setDateStr = set.set_date.split('T')[0];
      return setDateStr < todayStr; // Past due
    })
    .sort((a, b) => {
      // Sort by distance (closest first)
      const distA = getDistanceForSort(a.store.lat, a.store.lon);
      const distB = getDistanceForSort(b.store.lat, b.store.lon);
      return distA - distB;
    });

  // Available calls bucket state (nearby calls with remaining visits)
  const [availableCalls, setAvailableCalls] = useState<AvailableCall[]>([]);
  const [callsLoading, setCallsLoading] = useState(true);
  // #2675 — projects flagged always_available (Admin/Training/MISC/Store-Audit
  // time, etc.), pinned here so a rep/DM can log against them without a
  // scheduled route. Tapping one opens Manual Entry with the project pre-filled.
  const [alwaysAvailable, setAlwaysAvailable] = useState<Project[]>([]);
  const [totalAvailableCalls, setTotalAvailableCalls] = useState(0);

  // Monthly call progress (for header stats)
  const [routeSummary, setRouteSummary] = useState<RouteSummary | null>(null);

  // Month progress, preferring STORE coverage over my own work (#3454) — the
  // ring's job is telling the rep whether these stores still need visiting. An
  // older server that doesn't report coverage falls back to the prior numbers.
  const monthCalls = useMemo(() => {
    const completed = routeSummary?.total_visits_completed_store ?? routeSummary?.total_visits_completed ?? 0;
    return {
      completed,
      percentage: routeSummary?.completion_percentage_store ?? routeSummary?.completion_percentage ?? 0,
      sharedWithOthers: completed > (routeSummary?.total_visits_completed ?? 0),
    };
  }, [routeSummary]);

  // Multi-task detection: find stores that have both sets AND calls
  // This helps users identify stores where they can complete multiple tasks
  const storesWithSets = new Set(todaySets.map(s => s.store.legacy_store_id || s.store.store_id));
  const storesWithCalls = new Set(availableCalls.map(c => c.store.legacy_store_id || c.store.store_id));
  const multiTaskStores = new Set([...storesWithSets].filter(id => storesWithCalls.has(id)));

  // #2214 — project_id → requirement flags, harvested from the fetched sets/calls
  // (which carry the flags). Lets the active-visit + completed cards — whose
  // schedule data lacks the flags — still show pills by project lookup.
  const requirementsByProject = useMemo(() => {
    const m = new Map<number, ReturnType<typeof requirementsFromFlags>>();
    for (const s of todaySets) if (s.project_id) m.set(s.project_id, requirementsFromFlags(s));
    for (const c of availableCalls) if (c.project_id) m.set(c.project_id, requirementsFromFlags(c));
    return m;
  }, [todaySets, availableCalls]);

  /* COMMENTED OUT: Helper to convert km to miles - unused
  const kmToMiles = (km: number): number => km * 0.621371;
  */

  /* COMMENTED OUT: formatDistanceMiles - unused helper function
  const formatDistanceMiles = (lat: number | null, lon: number | null): string | null => {
    if (!userLocation || lat === null || lon === null) return null;
    const distanceKm = calculateDistance(userLocation.lat, userLocation.lng, lat, lon);
    if (distanceKm === null) return null;
    const miles = kmToMiles(distanceKm);
    if (miles < 0.1) return '< 0.1 mi';
    return `${miles.toFixed(1)} mi`;
  };
  */

  // Initialize stores from API. On error we render an empty list and let
  // the error banner below tell the user — no demo-data fallback (#1496).
  useEffect(() => {
    if (!isScheduleLoading) {
      if (scheduleError) {
        setStores([]);
      } else {
        // No error - use API stores (even if empty - that's valid).
        // Pass-through: ScheduledStore is the authoritative shape from
        // useSchedule; copying it lets us mutate via setStores locally
        // without affecting the hook's internal state.
        setStores(apiStores.map(s => ({ ...s })));
      }
    }
  }, [apiStores, isScheduleLoading, scheduleError]);

  // Fetch today's sets (prioritize SetDate, with window as grace period for past-due sets)
  // Refreshes when returning from a store visit (currentStoreId changes to null)
  useEffect(() => {
    async function fetchTodaySets() {
      const today = new Date();
      const todayStr = formatDateToLocal(today);

      // Cache-first paint (#3072): repaint the last-cached sets instantly (and
      // keep them when fully offline) — re-filtered for the CURRENT day, so a
      // stale cache never shows a set outside its date/window. An empty cached
      // array is a VALID cache hit (rep simply has no pending sets), not a miss.
      let hadCache = false;
      try {
        const cached = await getCachedTodaySets<ScheduledSet[]>();
        if (cached) {
          setTodaySets(filterSetsForToday(cached, todayStr));
          setSetsLoading(false);
          hadCache = true;
        }
      } catch {
        /* no usable cache — fall through with the skeleton up */
      }
      if (!hadCache) setSetsLoading(true);

      try {
        // Fetch sets within a wider range to capture window-based filtering
        const startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 14); // 2 weeks ago
        const endDate = new Date(today);
        endDate.setDate(endDate.getDate() + 14); // 2 weeks ahead

        const data = await setsApi.getMySets({
          start_date: formatDateToLocal(startDate),
          end_date: formatDateToLocal(endDate),
          status: 'pending', // Only pending sets for today's view
        });

        // Write-through the RAW window so a later offline boot can re-filter
        // it for whatever day it loads on (#3072).
        const rawSets = data.sets || [];
        cacheTodaySets(rawSets).catch(() => {
          /* best-effort cache write */
        });

        setTodaySets(filterSetsForToday(rawSets, todayStr));
      } catch (err) {
        console.error('Failed to fetch today sets:', err);
        // Silently fail — the cached paint (if any) stays up; sets are optional
      } finally {
        setSetsLoading(false);
      }
    }
    fetchTodaySets();
  }, [dayData.currentStoreId, refreshNonce]);

  // Fetch available calls (nearby calls bucket sorted by distance)
  useEffect(() => {
    async function fetchAvailableCalls() {
      // Cache-first paint (#2058): show the last-known Nearby Calls instantly
      // so a revisit isn't blank while the slow (~1.5-2.4s) API revalidates.
      let hadCache = false;
      try {
        const cached = await getCachedAvailableCalls<{ calls: AvailableCall[]; total: number }>();
        // An empty cached payload is a VALID hit (no nearby calls remaining),
        // not a miss — same defined-value check as useSchedule/#2068 (#3072).
        if (cached) {
          setAvailableCalls(cached.calls ?? []);
          setTotalAvailableCalls(cached.total ?? cached.calls?.length ?? 0);
          setCallsLoading(false);
          hadCache = true;
        }
      } catch {
        /* no usable cache — fall through with the skeleton up */
      }
      if (!hadCache) setCallsLoading(true);

      try {
        // Anchor preference: today's first set's store -> the GPS fix we
        // ALREADY request on mount (userLocation) -> none (server falls back
        // to the rep's home location).
        //
        // #2058: we no longer block here on a fresh high-accuracy
        // getCurrentPosition — that added ~2s of blank screen before the
        // Nearby Calls could even be requested. The mount effect already kicks
        // off a GPS fix; when it resolves, userLocation changes and this effect
        // re-runs to refine the anchor. Until then we fetch immediately with
        // the set anchor or the server-side home-location fallback.
        let anchorLat: number | undefined;
        let anchorLon: number | undefined;

        if (todaySets.length > 0 && todaySets[0].store) {
          const firstSetStore = todaySets[0].store;
          if (firstSetStore.lat && firstSetStore.lon) {
            anchorLat = Number(firstSetStore.lat);
            anchorLon = Number(firstSetStore.lon);
          }
        }
        if ((anchorLat === undefined || anchorLon === undefined) && userLocation) {
          anchorLat = userLocation.lat;
          anchorLon = userLocation.lng;
        }

        const data = await availableCallsApi.getAvailableCalls({
          anchor_lat: anchorLat,
          anchor_lon: anchorLon,
          limit: 10,
        });

        setAvailableCalls(data.calls || []);
        setTotalAvailableCalls(data.total_available || 0);
        cacheAvailableCalls({ calls: data.calls || [], total: data.total_available || 0 }).catch(
          () => {
            /* best-effort cache write */
          }
        );
      } catch (err) {
        console.error('Failed to fetch available calls:', err);
        // Silently fail - calls are optional
      } finally {
        setCallsLoading(false);
      }
    }

    // Wait for sets to load first so we can use them as anchor points
    if (!setsLoading) {
      fetchAvailableCalls();
    }
  }, [setsLoading, todaySets, userLocation, refreshNonce]);

  // #2675 — fetch the always-available (pinned) projects. Static-ish + tiny, so
  // a plain fetch is fine; failures are silent (the section just stays empty).
  // The endpoint returns a bare Project[] but we defensively unwrap the
  // {status,message,data} envelope in case the base renderer wraps it.
  useEffect(() => {
    let cancelled = false;
    projectsApi
      .getAlwaysAvailableProjects()
      .then((raw) => {
        if (cancelled) return;
        const arr = raw as unknown as Project[] | [{ data?: Project[] }];
        let list: Project[] = [];
        if (Array.isArray(arr) && arr.length > 0) {
          const first = arr[0] as { data?: Project[] };
          list = first && typeof first === 'object' && Array.isArray(first.data)
            ? first.data
            : (arr as Project[]);
        }
        setAlwaysAvailable(list.filter((p) => typeof p.projectid === 'number'));
      })
      .catch(() => {
        /* optional section — stay empty on error */
      });
    return () => {
      cancelled = true;
    };
  }, [refreshNonce]);

  // Fetch completed sets for today (for header stats)
  // Refreshes when returning from a store visit (currentStoreId changes)
  useEffect(() => {
    async function fetchCompletedSets() {
      try {
        const today = formatDateToLocal(new Date());
        const data = await setsApi.getMySets({
          start_date: today,
          end_date: today,
          status: 'completed',
        });
        setCompletedSetsToday((data.sets || []).length);
      } catch (err) {
        console.error('Failed to fetch completed sets:', err);
        // Silently fail - stats are optional
      }
    }
    fetchCompletedSets();
  }, [dayData.currentStoreId, refreshNonce]);

  // (calls-logged-today is derived from `stores` above — no separate fetch.)

  // Fetch monthly call progress (route summary)
  useEffect(() => {
    async function fetchRouteSummary() {
      try {
        const data = await routesApi.getMyRoutes();
        setRouteSummary(data.summary);
      } catch (err) {
        console.error('Failed to fetch route summary:', err);
        // Silently fail - stats are optional
      }
    }
    fetchRouteSummary();
  }, [dayData.currentStoreId, refreshNonce]); // Refresh when returning from store visit / pull-to-refresh

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Pull-to-refresh (#2058): revalidate the schedule (cache-first hook) and
  // re-run the section fetchers (sets/calls/route) by bumping the nonce.
  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      setRefreshNonce((n) => n + 1);
      await refetchSchedule();
    } finally {
      setIsRefreshing(false);
    }
  }, [refetchSchedule]);

  // #2089: the pull gesture + indicator moved to the app-shell provider;
  // Today just registers what "refresh" means here.
  usePageRefresh(handleRefresh);

  // Update store status based on day tracking
  useEffect(() => {
    if (dayData.currentStoreId) {
      setStores((prev) =>
        prev.map((s) =>
          s.id === dayData.currentStoreId
            ? { ...s, status: 'in_progress' as const }
            : s.status === 'in_progress'
            ? { ...s, status: 'pending' as const }
            : s
        )
      );
      // Also update in the schedule hook
      updateStoreStatus(dayData.currentStoreId, 'in_progress');
    }
  }, [dayData.currentStoreId, updateStoreStatus]);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
    } else {
      await promptInstall();
    }
  };

  // Show location permission first, then odometer entry
  const handleStartDayClick = () => {
    setShowLocationPrompt(true);
  };

  // A one-shot position with a short leash — the at-store decision needs
  // where the rep IS, and the mount-time fix can be an hour and a commute old.
  const freshPosition = (): Promise<{ lat: number; lng: number } | null> =>
    new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => resolve(null),
        { timeout: 6000, maximumAge: 30000 },
      );
    });

  // Called when location permission is resolved. Clocking in confirms a
  // status, it never assumes one: at a store, the store page owns the
  // clock-in and no drive is invented; away, the drive is declared through
  // the odometer step; location unknown, the rep is asked rather than
  // guessed at.
  const handleLocationReady = async (hasLocation: boolean) => {
    setHasLocationPermission(hasLocation);
    setShowLocationPrompt(false);

    const here = (await freshPosition()) ?? userLocation;
    if (here) setUserLocation(here);

    const surfaces: TodayStoreRef[] = [
      // Rows without a legacy id can't be routed to a store page — offering
      // them would land the rep on store-not-found (or a colliding store).
      ...[...todaySets, ...availableCalls]
        .filter((row) => row.store.legacy_store_id != null)
        .map((row) => ({
          name: getStoreDisplayName(row.store),
          legacyStoreId: row.store.legacy_store_id as string,
          projectId: row.project_id,
          lat: row.store.lat,
          lon: row.store.lon,
        })),
      // The schedule keeps a completed visit's store on the list, so a second
      // session started while still standing there is recognized too.
      ...stores
        .filter((s) => s.id > 0)
        .map((s) => ({
          name: s.name,
          legacyStoreId: s.id,
          projectId: s.projectIds[0],
          lat: s.lat,
          lon: s.lng,
        })),
    ];
    const candidate = storeAtHand(here, surfaces);
    if (candidate) {
      setClockInFork({ candidate });
      return;
    }
    if (!hasLocation || !here) {
      setClockInFork({ candidate: null });
      return;
    }
    // Away from every store: the drive is real, declare it through the
    // odometer step.
    setOdometerEntryType('start_day');
    setShowOdometerEntry(true);
  };

  // Called when odometer is entered for clocking in
  const handleStartDayOdometer = async (reading: number) => {
    await startDayOdometer(reading);
    await clockIn();
    setShowOdometerEntry(false);
    setHasLocationPermission(null);
    // Automatically start driving after clock-in (no delay needed)
    startDriving();
  };

  // Cancel the start day flow
  const handleCancelStartDay = () => {
    setShowLocationPrompt(false);
    setShowOdometerEntry(false);
    setHasLocationPermission(null);
  };

  // Skip odometer tracking and clock in directly
  const handleSkipOdometer = async () => {
    await clockIn();
    setShowOdometerEntry(false);
    setHasLocationPermission(null);
    // Automatically start driving after clock-in (no delay needed)
    startDriving();
  };

  // Show odometer entry before clocking out
  const handleEndDayClick = () => {
    setOdometerEntryType('end_day');
    setShowOdometerEntry(true);
  };

  // Helper to patch last call log with return drive data.
  // `startOdometer` is the odo at the moment the rep left the last store — if not
  // passed, we fall back to the last non-end-day reading from OdometerContext.
  // `endOdometer` is the end-of-day reading (optional if Skip Odometer path).
  const patchReturnDrive = async (endOdometer?: number, startOdometerOverride?: number) => {
    const lastStoreEntry = getLastCompletedStoreEntry();
    if (!lastStoreEntry?.callLogId || !lastStoreEntry.endTime) {
      console.log('No completed store visit with callLogId to patch with return drive');
      return;
    }

    // Find the drive entry that started after the last store checkout (return drive)
    const returnDrive = dayData.timeEntries.find(
      (e) => e.type === 'drive' && e.startTime >= lastStoreEntry.endTime!
    );

    // Get return drive start time (= last store checkout time)
    const returnDriveStart = lastStoreEntry.endTime;
    const returnDriveEnd = new Date();

    // Resolve drivebackodostart. Preference order:
    //   1. explicit override (caller captured pre-end-day value)
    //   2. the return-drive entry's own startOdometer (reserved for future when
    //      drive entries track odos directly)
    //   3. lastStoreEntry.endOdometer (captured at Leave Store if present)
    //   4. odometerData.currentReading — the last recorded reading before end-day.
    //      This is the running odometer maintained by OdometerContext across
    //      start_day / store_arrival / store_departure events, so it always
    //      reflects where the rep last left off — which IS the drive-back origin.
    // The server rejects a drive-back payload missing either odo, so this
    // fallback is critical to #1507 landing a useful row. (#1507 follow-up)
    const drivebackodostart =
      startOdometerOverride
      ?? returnDrive?.startOdometer
      ?? lastStoreEntry.endOdometer
      ?? odometerData.currentReading
      ?? undefined;

    const formatTimeToISO = (date: Date) => toNaiveLocal(date.toISOString());

    try {
      // Queue-only (#1987): the offline sync queue is the SOLE server-write
      // path. We no longer fire a direct PATCH here. The day entry's callLogId
      // is stamped best-effort once the check-in syncs (StoreDetail bounded
      // effect), but a local visit row may not carry it yet (or this may be a
      // server-sourced visit), so resolveVisitLocalId can return null. Adopt
      // the CallLog (idempotent on calllogid — returns the existing check-in
      // row's uuid when present) to GUARANTEE a valid visitLocalId, then
      // enqueue a single patchReturnDrive op carrying ALL four return-drive
      // fields with the server's wire names (drivebackodostart /
      // drivebackodoend — the prior op mis-named these and the server dropped
      // them).
      const { writeOps, adoptServerCallLog } = await import('../sync/writeOps');
      const visitLocalId = await adoptServerCallLog(lastStoreEntry.callLogId, {
        storeId: lastStoreEntry.storeId,
      });
      await writeOps.patchReturnDrive.apply({
        visitLocalId,
        stdrivetimeback: formatTimeToISO(returnDriveStart),
        eddrivetimeback: formatTimeToISO(returnDriveEnd),
        drivebackodostart,
        drivebackodoend: endOdometer,
      });
      console.log('Enqueued return drive patch for call log', lastStoreEntry.callLogId, {
        visitLocalId,
        drivebackodostart,
        drivebackodoend: endOdometer,
      });
    } catch (err) {
      // Surface the error so a failed enqueue doesn't silently lose data (#1507)
      const apiErr = err as { status?: number; data?: unknown; message?: string };
      console.error('[patchReturnDrive] enqueue failed', {
        status: apiErr.status,
        data: apiErr.data,
        message: apiErr.message,
        callLogId: lastStoreEntry.callLogId,
        payload: { drivebackodostart, drivebackodoend: endOdometer },
      });
    }
  };

  // Called when odometer is entered for ending day.
  // CRITICAL: capture currentReading BEFORE endDayOdometer runs — that call
  // overwrites currentReading with the end-of-day value, which would make our
  // drivebackodostart fall back to the end-day value and produce 0-mile legs.
  const handleEndDayOdometer = async (reading: number) => {
    const startOdo = odometerData.currentReading ?? undefined;
    // First record the odometer (updates currentReading to `reading`)
    await endDayOdometer(reading);
    // Patch return drive to last call log with the snapshotted start odo
    patchReturnDrive(reading, startOdo).catch(() => {});
    // Then close the clock-in window
    await clockOut();
    setShowOdometerEntry(false);
  };

  // Skip ending odometer and just end the day
  const handleSkipEndDayOdometer = async () => {
    // Still try to patch return drive — drivebackodoend will be undefined, so
    // the server will likely reject, but the enhanced console.error will tell us.
    patchReturnDrive().catch(() => {});
    await clockOut();
    setShowOdometerEntry(false);
  };

  // Use day tracking totals when available
  const totalMinutes = dayData.status !== 'not_started'
    ? dayData.totalDriveMinutes + dayData.totalStoreMinutes
    : stores.filter((s) => s.duration).reduce((acc, s) => acc + (s.duration || 0), 0);

  const formattedDate = currentTime.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Pull-to-refresh indicator now renders globally from
          PullToRefreshProvider (#2089). */}
      {/* Header */}
      <PageHeader
        title="Today's Schedule"
        subtitle={formattedDate}
        actions={
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            aria-label="Refresh"
            className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/20 transition-colors hover:bg-white/30 disabled:opacity-60"
          >
            <svg
              className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        }
      >
        {/* Day Status Banner */}
        {dayData.status === 'not_started' && !showOdometerEntry && !showLocationPrompt && (
          <div className="mt-4 rounded-xl bg-white/20 p-4 backdrop-blur-sm">
            <p className="mb-3 text-center text-sm text-primary-100">
              Ready to start? Clock in to begin tracking your time.
            </p>
            <button
              onClick={handleStartDayClick}
              disabled={isDayLoading}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-white py-3 font-semibold text-primary-700 shadow transition-all hover:bg-primary-50 active:scale-[0.98] disabled:opacity-70"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Clock In</span>
            </button>
          </div>
        )}

        {/* Location Permission Prompt — also reachable from a clocked-out day,
            where clocking in opens the next window. */}
        {dayData.status !== 'active' && showLocationPrompt && (
          <div className="mt-4">
            <LocationPermission
              onLocationReady={handleLocationReady}
              onCancel={handleCancelStartDay}
            />
          </div>
        )}

        {/* Odometer Entry for Clock In */}
        {dayData.status !== 'active' && showOdometerEntry && odometerEntryType === 'start_day' && (
          <div className="mt-4">
            {hasLocationPermission === false && (
              <div className="mb-3 rounded-lg bg-amber-100 p-3 text-sm text-amber-800">
                <div className="flex items-center gap-2">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span>Continuing without GPS - mileage estimates won't be available</span>
                </div>
              </div>
            )}
            <OdometerEntry
              type="start_day"
              onSubmit={handleStartDayOdometer}
              onCancel={handleCancelStartDay}
              onSkip={handleSkipOdometer}
              isLoading={isOdometerLoading || isDayLoading}
            />
          </div>
        )}

        {/* Active Driving Banner */}
        {dayData.status === 'active' && dayData.currentActivity === 'driving' && dayData.startTime && (
          <div className="mt-4 rounded-xl bg-blue-500/30 p-4 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500">
                  <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-white">Currently Driving</p>
                  <p className="text-xs text-blue-100">
                    Clocked in at {dayData.startTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-lg font-bold text-white">
                    <LiveTimer startTime={openDriveLeg?.startTime || dayData.startTime} />
                  </div>
                  <p className="text-xs text-blue-100">Drive Time</p>
                </div>
                <button
                  onClick={() => stopDriving()}
                  className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 transition-all hover:bg-white/30 active:scale-95"
                  title="Take a Break"
                >
                  <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="6" y="4" width="4" height="16" rx="1" />
                    <rect x="14" y="4" width="4" height="16" rx="1" />
                  </svg>
                </button>
              </div>
            </div>
            {/* What the rep just finished, at a glance — leaving a store closes
                its leg and opens this drive, and neither should need a modal. */}
            {storeJustLeft?.endTime && (
              <div className="mt-3 flex items-center justify-between border-t border-white/20 pt-2 text-xs text-blue-50">
                <span className="truncate">
                  Left {storeJustLeft.storeName || 'store'} at{' '}
                  {storeJustLeft.endTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </span>
                <span className="ml-2 flex-shrink-0 font-medium">
                  {formatCompactMinutes(storeJustLeft.durationMinutes ?? 0)} in store
                </span>
              </div>
            )}
          </div>
        )}

        {/* Idle/Break Banner - when day is active but not driving or in store */}
        {/* Only show "On Break" if user has completed at least one activity (has ended entries) */}
        {/* Otherwise they just started their day and showing "On Break" is confusing */}
        {dayData.status === 'active' && dayData.currentActivity === 'idle' && dayData.startTime &&
         dayData.timeEntries.some(e => e.endTime) && (
          <div className="mt-4 rounded-xl bg-slate-500/30 p-4 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-500">
                  <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-white">On Break</p>
                  <p className="text-xs text-slate-200">
                    Tap resume when ready to continue
                  </p>
                </div>
              </div>
              <button
                onClick={() => startDriving()}
                className="flex items-center gap-2 rounded-lg bg-white/20 px-4 py-2 text-sm font-medium text-white transition-all hover:bg-white/30 active:scale-95"
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
                Resume
              </button>
            </div>
          </div>
        )}

        {/* In Store Banner */}
        {dayData.status === 'active' && dayData.currentActivity === 'in_store' && (() => {
          const currentStoreEntry = openStoreLeg;
          const currentStoreName = currentStoreEntry?.storeName || 'Store';
          const driveMiles = legMiles(driveToHere);
          const currentStoreId = currentStoreEntry?.storeId || dayData.currentStoreId;
          const currentProjectId = currentStoreEntry?.projectId || dayData.currentProjectId;

          // CRITICAL: Warn if projectId is missing - this causes wrong store navigation
          if (!currentProjectId && currentStoreId) {
            console.warn('[Today] Missing projectId for store navigation! storeId:', currentStoreId, 'entry:', currentStoreEntry);
          }

          // Build URL - ALWAYS include projectId (use 0 as fallback to indicate issue)
          const storeUrl = currentStoreId
            ? `/store/${currentStoreId}?projectId=${currentProjectId || 0}`
            : '#';
          const checkoutUrl = currentStoreId
            ? `/store/${currentStoreId}?projectId=${currentProjectId || 0}&checkout=true`
            : '#';
          return (
            <div className="mt-4 rounded-xl bg-green-500/30 p-4 backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <Link
                  to={storeUrl}
                  className="flex flex-1 items-center gap-3 transition-all hover:opacity-80"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-500">
                    <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{currentStoreName}</p>
                    {activeSiteTask ? (
                      <p className="text-xs text-green-50">{`#${activeSiteTask.projectId} ${activeSiteTask.projectName}`}</p>
                    ) : (
                      <p className="text-xs text-green-100">Tap to return</p>
                    )}
                  </div>
                </Link>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-lg font-bold text-white">
                      <LiveTimer startTime={currentStoreEntry?.startTime || new Date()} />
                    </div>
                    <p className="text-xs text-green-100">Store Time</p>
                  </div>
                  <Link
                    to={checkoutUrl}
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 transition-all hover:bg-white/30 active:scale-95"
                    title="Leave Store"
                  >
                    <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </Link>
                </div>
              </div>
              {/* The drive that ended here, at a glance. Miles are a GPS
                  estimate the rep can correct on the Time page, not odometer
                  truth — labelled so it never reads as measured. */}
              {driveToHere?.endTime && (
                <div className="mt-3 flex items-center justify-between border-t border-white/20 pt-2 text-xs text-green-50">
                  <span>
                    Drove here · {formatCompactMinutes(driveToHere.durationMinutes ?? 0)}
                  </span>
                  {driveMiles !== null && (
                    <span className="ml-2 flex-shrink-0 font-medium">{driveMiles} mi</span>
                  )}
                </div>
              )}
              {siteTasks.length > 1 && (
                <div className="mt-3 space-y-1 border-t border-white/20 pt-2">
                  {siteTasks.map((t) => (
                    <Link
                      key={t.projectId}
                      // Carry projectDesc so the resumed task keeps its real
                      // name — without it StoreDetail rebuilds a synthetic task
                      // whose name falls back to "#<id>" and overwrites the
                      // session's good name (diag #40 / #2002 follow-up).
                      to={`/store/${currentStoreId}?projectId=${t.projectId}&taskType=call&projectDesc=${encodeURIComponent(t.projectName)}`}
                      className="flex items-center justify-between rounded-lg px-2 py-1 text-xs text-white/90 hover:bg-white/10"
                    >
                      <span className="flex items-center gap-2 truncate">
                        <span aria-hidden>
                          {t.status === 'active' ? '●' : t.status === 'paused' ? '⏸' : t.status === 'completed' ? '✓' : '○'}
                        </span>
                        <span className="truncate">{`#${t.projectId} ${t.projectName}`}</span>
                      </span>
                      {/* Per-project sub-total inside this visit. The running
                          one ticks from the time already banked by earlier
                          sessions of the same project. */}
                      {(() => {
                        const pt = projectTimes.get(t.projectId);
                        if (!pt) return <span className="text-white/60">{t.status}</span>;
                        if (pt.runningSince) {
                          return (
                            <span className="flex-shrink-0 font-medium text-white">
                              <LiveTimer startTime={pt.runningSince} baseSeconds={pt.minutes * 60} compact />
                            </span>
                          );
                        }
                        if (pt.minutes > 0) {
                          return (
                            <span className="flex-shrink-0 text-white/80">
                              {formatCompactMinutes(pt.minutes)}
                            </span>
                          );
                        }
                        return <span className="text-white/60">{t.status}</span>;
                      })()}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* Summary Stats - only show when day is active */}
        {dayData.status !== 'not_started' && (
          <div className="mt-4 flex gap-2">
            <div className="flex-1 rounded-xl bg-white/10 p-3 text-center backdrop-blur-sm">
              <div className="text-2xl font-bold">{completedSetsToday}/{todayScheduledSets.length}</div>
              <div className="text-xs text-primary-200">Sets</div>
            </div>
            {/* Calls for the month. The ring counts STORE coverage (#3454):
                the question it answers is whether these stores still need
                visiting, which is what stops two reps driving to the same one.
                My own share is named underneath when a colleague contributed,
                so the rep can still see what they did. */}
            <div className="flex-1 rounded-xl bg-white/10 p-3 text-center backdrop-blur-sm">
              <div className="text-2xl font-bold">
                {routeSummary ? `${monthCalls.completed}/${routeSummary.total_visits_required}` : callsLoggedToday}
              </div>
              {routeSummary && routeSummary.total_visits_required > 0 && (
                <div className="mx-auto mt-1 h-1.5 w-full max-w-[60px] overflow-hidden rounded-full bg-white/20">
                  <div
                    className="h-full rounded-full bg-white transition-all"
                    style={{ width: `${Math.min(monthCalls.percentage, 100)}%` }}
                  />
                </div>
              )}
              <div className="text-xs text-primary-200">
                {routeSummary ? (monthCalls.sharedWithOthers ? `You: ${routeSummary.total_visits_completed}` : 'Calls (Month)') : 'Calls'}
              </div>
            </div>
            <div className="flex-1 rounded-xl bg-white/10 p-3 text-center backdrop-blur-sm">
              <div className="text-2xl font-bold">{formatTime(totalMinutes)}</div>
              <div className="text-xs text-primary-200">Hours</div>
            </div>
            <div className="flex-1 rounded-xl bg-white/10 p-3 text-center backdrop-blur-sm">
              <div className="text-2xl font-bold">{odometerData.totalMiles || dayData.totalMiles || 0}</div>
              <div className="text-xs text-primary-200">Miles</div>
            </div>
          </div>
        )}
      </PageHeader>

      {/* Store List */}
      <main className="p-4">
        {/* Loading State */}
        {isScheduleLoading && stores.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
          </div>
        )}

        {/* Sets / Calls section skeletons (#2058) — placeholders while those
            sections load so the page doesn't look empty mid-fetch. */}
        {(setsLoading || callsLoading) &&
          todaySets.length === 0 &&
          availableCalls.length === 0 &&
          !scheduleError && (
            <div className="space-y-3" aria-hidden="true">
              {[0, 1].map((i) => (
                <div key={i} className="animate-pulse rounded-xl bg-white p-4 shadow-card">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 flex-shrink-0 rounded-full bg-slate-200"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-2/3 rounded bg-slate-200"></div>
                      <div className="h-3 w-1/3 rounded bg-slate-100"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

        {/* API Error Notice — explicit failure state, no demo data (#1496). */}
        {scheduleError && (
          <div className="mb-4 rounded-lg bg-amber-50 p-4 text-sm text-amber-700">
            <div className="flex items-start gap-2">
              <svg className="h-5 w-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1">
                <p className="font-medium">Couldn't load your schedule from the server</p>
                <p className="mt-1 text-amber-600">
                  The server may be temporarily unavailable. Your in-progress visit data is safe on this device. Check the connection chip in the header for status.
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-2 rounded bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Clock Out — at the top of Today while the rep is clocked in so it's
            reachable without scrolling past every event. The odometer entry
            replaces the button in place; the clocked-out summary renders
            here too. */}
        {dayData.status === 'active' && !showOdometerEntry && (
          <div className="mb-4">
            <button
              onClick={handleEndDayClick}
              disabled={isDayLoading}
              className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-slate-300 bg-white py-4 font-semibold text-slate-700 shadow-sm transition-all hover:border-slate-400 hover:bg-slate-50 active:scale-[0.98] disabled:opacity-70"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
              </svg>
              <span>Clock Out</span>
            </button>
          </div>
        )}

        {dayData.status === 'active' && showOdometerEntry && odometerEntryType === 'end_day' && (
          <div className="mb-4">
            <OdometerEntry
              type="end_day"
              lastReading={odometerData.currentReading}
              // Same GPS suggestion the store-arrival prompt gets: the rep
              // usually drove home, so lead with an estimate they confirm or
              // correct rather than an empty box.
              calculatedReading={
                userLocation ? calculateExpectedOdometer(userLocation.lat, userLocation.lng) : null
              }
              onSubmit={handleEndDayOdometer}
              onCancel={() => setShowOdometerEntry(false)}
              onSkip={handleSkipEndDayOdometer}
              isLoading={isOdometerLoading || isDayLoading}
            />
          </div>
        )}

        {dayData.status === 'ended' && dayData.startTime && dayData.endTime &&
         !showOdometerEntry && !showLocationPrompt && (
          <div className="mb-4 rounded-xl bg-green-50 p-4">
            <div className="mb-3 flex items-center justify-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h3 className="text-center font-semibold text-green-800">Clocked Out</h3>
            <p className="mt-1 text-center text-sm text-green-600">
              {dayData.startTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} - {dayData.endTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            </p>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-white p-2">
                <div className="font-bold text-green-700">{formatTime(dayData.totalStoreMinutes)}</div>
                <div className="text-xs text-slate-500">Store Time</div>
              </div>
              <div className="rounded-lg bg-white p-2">
                <div className="font-bold text-blue-700">{formatTime(dayData.totalDriveMinutes)}</div>
                <div className="text-xs text-slate-500">Drive Time</div>
              </div>
              <div className="rounded-lg bg-white p-2">
                <div className="font-bold text-slate-700">{odometerData.totalMiles || dayData.totalMiles || 0} mi</div>
                <div className="text-xs text-slate-500">Miles</div>
              </div>
            </div>
            {/* Going back out is a normal split shift, not a correction: this
                opens the next clock-in window and today's totals keep adding up. */}
            <button
              onClick={handleStartDayClick}
              disabled={isDayLoading}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-white py-3 font-semibold text-green-700 shadow-sm transition-all hover:bg-green-100 active:scale-[0.98] disabled:opacity-70"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Clock In Again</span>
            </button>
          </div>
        )}


        {/* Active Visit — rendered with TaskCard for visual consistency
            with the pending sections below (#1954). One card representing
            the store visit currently in progress. */}
        {(() => {
          const activeStore = stores.find((s) => s.status === 'in_progress');
          if (!activeStore) return null;
          // Resolve the project as a single (id, name) PAIR — never mix a
          // positional name with a different id (#1996). The old code took
          // id from dayData.currentProjectId but the name from
          // activeStore.projects[0], so a visit on #1102 rendered as
          // "#1102 PRO HAIR CARE" (1068's name) in diag #39. Prefer the
          // authoritative active SiteTask, then the SiteTask matching
          // day-tracking's project, then an id-matched lookup in the store's
          // own schedule arrays.
          const fallbackPid = dayData.currentProjectId ?? activeStore.projectIds[0] ?? 0;
          const matchTask =
            activeSiteTask ??
            siteTasks.find((t) => t.projectId === dayData.currentProjectId) ??
            siteTasks.find((t) => t.projectId === fallbackPid) ??
            null;
          const projectId = matchTask?.projectId ?? fallbackPid;
          const pidIdx = activeStore.projectIds.indexOf(projectId);
          const projectDescription =
            matchTask?.projectName ??
            (pidIdx >= 0 ? activeStore.projects[pidIdx] : undefined) ??
            `#${projectId}`;
          if (!projectId) {
            console.warn('[Today] Missing projectId for active store nav. storeId:', activeStore.id);
          }
          // Derive a store_name + retailer abbreviation from the merged
          // schedule name format "RetailerAbbrev #StoreNumber".
          const nameMatch = activeStore.name.match(/^(\S+)\s*#?(\S+)?/);
          return (
            <div className="mb-4">
              <h2 className="mb-2 text-sm font-medium uppercase tracking-wide text-slate-500">
                Active Visit
              </h2>
              <TaskCard
                type="call"
                store={{
                  store_id: activeStore.id,
                  store_name: activeStore.name,
                  retailer_abbreviation: nameMatch?.[1],
                  legacy_store_id: nameMatch?.[2] ? parseInt(nameMatch[2], 10) : undefined,
                  address: activeStore.address,
                  city: activeStore.city,
                  lat: activeStore.lat || null,
                  lon: activeStore.lng || null,
                }}
                projectId={projectId || undefined}
                projectDescription={projectDescription}
                status="pending"
                requirements={projectId ? requirementsByProject.get(projectId) : undefined}
                isActive
                userLocation={userLocation}
                onClick={() => navigate(`/store/${activeStore.id}?projectId=${projectId || 0}`)}
              />
            </div>
          );
        })()}

        {/* Today's Scheduled Sets - Primary work list */}
        {!setsLoading && todayScheduledSets.length > 0 && (
          <div className="mb-4">
            <h2 className="mb-2 text-sm font-medium uppercase tracking-wide text-slate-500">
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-amber-500"></span>
                Today's Sets
                <span className="text-xs font-normal text-slate-400">({todayScheduledSets.length})</span>
              </span>
            </h2>
            <div className="space-y-3">
              {todayScheduledSets.map((set) => {
                const storeIdForNav = set.store.legacy_store_id || set.store.store_id;
                const isActiveStore = dayData.currentStoreId === storeIdForNav && dayData.currentActivity === 'in_store';
                const hasMultipleTasks = multiTaskStores.has(storeIdForNav);
                return (
                  <div key={set.projectset_id}>
                    <TaskCard
                      type="set"
                      store={{
                        store_id: set.store.store_id,
                        store_name: getStoreDisplayName(set.store),
                        legacy_store_id: set.store.legacy_store_id ? parseInt(set.store.legacy_store_id, 10) : undefined,
                        address: set.store.address,
                        city: set.store.city,
                        state: set.store.state,
                        lat: set.store.lat,
                        lon: set.store.lon,
                      }}
                      projectId={set.project_id}
                      projectDescription={set.project_description}
                      status="pending"
                      onClick={() => navigate(`/set/${set.projectset_id}`)}
                      setDate={set.set_date}
                      setHours={set.set_hours ?? undefined}
                      windowStart={set.display_specs?.window_start?.split('T')[0]}
                      windowEnd={set.display_specs?.window_end?.split('T')[0]}
                      setTypeName={set.set_type_name ?? undefined}
                      team={set.team}
                      requirements={requirementsFromFlags(set)}
                      isActive={isActiveStore}
                      userLocation={userLocation}
                    />
                    {hasMultipleTasks && (
                      <div className="mt-1 ml-4 text-xs text-cyan-600 font-medium">
                        + Call available at this store
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <button
              onClick={() => navigate('/sets')}
              className="mt-3 w-full rounded-lg bg-amber-50 py-2 text-sm font-medium text-amber-700 hover:bg-amber-100"
            >
              View All My Sets
            </button>
          </div>
        )}

        {/* Overdue Sets - Collapsible section for past-due sets still in window */}
        {!setsLoading && overdueSets.length > 0 && (
          <div className="mb-4">
            <button
              onClick={() => setOverdueCollapsed(!overdueCollapsed)}
              className="mb-2 flex w-full items-center justify-between text-sm font-medium uppercase tracking-wide text-slate-500"
            >
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-red-500"></span>
                Overdue Sets
                <span className="text-xs font-normal text-red-500">({overdueSets.length})</span>
              </span>
              <svg
                className={`h-4 w-4 transition-transform ${overdueCollapsed ? '' : 'rotate-180'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {!overdueCollapsed && (
              <div className="space-y-3">
                {overdueSets.map((set) => {
                  const storeIdForNav = set.store.legacy_store_id || set.store.store_id;
                  const isActiveStore = dayData.currentStoreId === storeIdForNav && dayData.currentActivity === 'in_store';
                  const hasMultipleTasks = multiTaskStores.has(storeIdForNav);
                  return (
                    <div key={set.projectset_id}>
                      <TaskCard
                        type="set"
                        store={{
                          store_id: set.store.store_id,
                          store_name: getStoreDisplayName(set.store),
                          legacy_store_id: set.store.legacy_store_id ? parseInt(set.store.legacy_store_id, 10) : undefined,
                          address: set.store.address,
                          city: set.store.city,
                          state: set.store.state,
                          lat: set.store.lat,
                          lon: set.store.lon,
                        }}
                        projectId={set.project_id}
                        projectDescription={set.project_description}
                        status="overdue"
                        onClick={() => navigate(`/set/${set.projectset_id}`)}
                        setDate={set.set_date}
                        setHours={set.set_hours ?? undefined}
                        windowStart={set.display_specs?.window_start?.split('T')[0]}
                        windowEnd={set.display_specs?.window_end?.split('T')[0]}
                        setTypeName={set.set_type_name ?? undefined}
                        team={set.team}
                        requirements={requirementsFromFlags(set)}
                        isActive={isActiveStore}
                        userLocation={userLocation}
                      />
                      {hasMultipleTasks && (
                        <div className="mt-1 ml-4 text-xs text-cyan-600 font-medium">
                          + Call available at this store
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {overdueCollapsed && (
              <p className="text-xs text-slate-400 italic">
                Tap to expand and view {overdueSets.length} overdue set{overdueSets.length > 1 ? 's' : ''} still within their delivery window
              </p>
            )}
          </div>
        )}

        {/* Nearby Calls - Flexible bucket of available calls */}
        {!callsLoading && availableCalls.length > 0 && (
          <div className="mb-4">
            <h2 className="mb-2 text-sm font-medium uppercase tracking-wide text-slate-500">
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-cyan-500"></span>
                Nearby Calls
                {totalAvailableCalls > availableCalls.length && (
                  <span className="text-xs font-normal text-slate-400">
                    ({availableCalls.length} of {totalAvailableCalls})
                  </span>
                )}
              </span>
            </h2>
            <div className="space-y-3">
              {availableCalls.map((call) => {
                const storeIdForNav = call.store.legacy_store_id || call.store.store_id;
                const isActiveStore = dayData.currentStoreId === storeIdForNav && dayData.currentActivity === 'in_store';
                const hasMultipleTasks = multiTaskStores.has(storeIdForNav);
                return (
                  <div key={call.route_id}>
                    <TaskCard
                      type="call"
                      store={{
                        store_id: call.store.store_id,
                        store_name: getStoreDisplayName(call.store),
                        legacy_store_id: call.store.legacy_store_id ? parseInt(call.store.legacy_store_id, 10) : undefined,
                        address: call.store.address,
                        city: call.store.city,
                        state: call.store.state,
                        lat: call.store.lat,
                        lon: call.store.lon,
                      }}
                      projectId={call.project_id}
                      projectDescription={call.project_description}
                      status={call.visits_completed >= call.frequency ? 'completed' : 'pending'}
                      onClick={() => navigate(`/call/${call.route_id}`)}
                      visitsCompleted={call.visits_completed}
                      visitsRequired={call.frequency}
                      visitsCompletedStore={call.visits_completed_store}
                      completedBy={othersWhoCovered(call.completed_by, myAssociateId)}
                      hoursPerVisit={call.hours_per_visit ?? undefined}
                      lastVisitDate={call.last_visit_date ?? undefined}
                      requirements={requirementsFromFlags(call)}
                      isActive={isActiveStore}
                      userLocation={userLocation}
                    />
                    {hasMultipleTasks && (
                      <div className="mt-1 ml-4 text-xs text-amber-600 font-medium">
                        + Set available at this store
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {totalAvailableCalls > availableCalls.length && (
              <button
                onClick={() => navigate('/routes')}
                className="mt-3 w-full rounded-lg bg-cyan-50 py-2 text-sm font-medium text-cyan-700 hover:bg-cyan-100"
              >
                View All {totalAvailableCalls} Available Calls
              </button>
            )}
          </div>
        )}

        {/* No Work Available Message */}
        {!setsLoading && !callsLoading && todaySets.length === 0 && availableCalls.length === 0 && stores.filter(s => s.status !== 'completed').length === 0 && (
          <div className="rounded-xl bg-slate-100 p-8 text-center">
            <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-200">
              <svg className="h-6 w-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="font-semibold text-slate-700">All Caught Up!</h3>
            <p className="mt-1 text-sm text-slate-500">No sets or calls available for today.</p>
          </div>
        )}

        {/* Completed Today — one TaskCard PER COMPLETED TASK so a
            multi-task visit at one store shows up as separate cards
            (#1954 Option A). Rep visited PX#886 and finished 3 tasks ->
            3 cards in this section, each with its own project name and
            duration, instead of one squashed card listing all 3. */}
        {stores.some((s) => s.completedTasks.length > 0) && (
          <div className="mb-4">
            <h2 className="mb-2 text-sm font-medium uppercase tracking-wide text-slate-500">
              Completed Today
            </h2>
            <div className="space-y-3">
              {stores.flatMap((store) => {
                if (store.completedTasks.length === 0) return [];
                const nameMatch = store.name.match(/^(\S+)\s*#?(\S+)?/);
                // Format minutes -> human-readable label. "<1m" for very
                // short, "Nm" for under an hour, "Xh Ym" otherwise.
                // (#1954 follow-up: raw decimal hours leaked through the
                // hoursPerVisit prop and rendered as "0.0166666666h").
                const formatTaskDuration = (minutes: number | null): string | undefined => {
                  if (minutes == null) return undefined;
                  if (minutes < 1) return '< 1m';
                  if (minutes < 60) return `${Math.round(minutes)}m`;
                  const h = Math.floor(minutes / 60);
                  const m = Math.round(minutes % 60);
                  return m > 0 ? `${h}h ${m}m` : `${h}h`;
                };
                return store.completedTasks
                  // Earliest finished first so the timeline reads top->bottom
                  .slice()
                  .sort((a, b) => (a.checkInTime ?? '').localeCompare(b.checkInTime ?? ''))
                  .map((task) => (
                    <TaskCard
                      key={`completed-${task.callLogId}`}
                      type="call"
                      store={{
                        store_id: store.id,
                        store_name: store.name,
                        retailer_abbreviation: nameMatch?.[1],
                        legacy_store_id: nameMatch?.[2] ? parseInt(nameMatch[2], 10) : undefined,
                        address: store.address,
                        city: store.city,
                        lat: store.lat || null,
                        lon: store.lng || null,
                      }}
                      projectId={task.projectId}
                      projectDescription={task.projectName}
                      status="completed"
                      requirements={task.projectId ? requirementsByProject.get(task.projectId) : undefined}
                      durationLabel={formatTaskDuration(task.duration)}
                      onClick={() =>
                        navigate(`/store/${store.id}?projectId=${task.projectId || 0}&callLogId=${task.callLogId}`)
                      }
                    />
                  ));
              })}
            </div>
          </div>
        )}

        {/* GPS Error Notice */}
        {dayError && (
          <div className="mt-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-700">
            <div className="flex items-start gap-2">
              <svg className="h-5 w-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>{dayError} - Time tracking will continue without GPS location.</span>
            </div>
          </div>
        )}

        {/* #2675 — Always Available projects: standing/admin time projects an
            admin flagged (always_available), pinned here so a rep/DM can log
            against them without a scheduled route. Tapping opens Manual Entry
            with the project pre-filled (they only pick the store + time). */}
        {alwaysAvailable.length > 0 && (
          <div className="mt-6 mb-4">
            <h2 className="mb-2 text-sm font-medium uppercase tracking-wide text-slate-500">
              <span className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-violet-500"></span>
                Always Available
              </span>
            </h2>
            <div className="space-y-2">
              {alwaysAvailable.map((p) => {
                const label = (p.projectdesc && p.projectdesc.trim())
                  || (p.projectname && p.projectname.trim())
                  || `Project ${p.projectid}`;
                return (
                  <button
                    key={p.projectid}
                    type="button"
                    onClick={() =>
                      navigate(`/manual-entry/new?projectId=${p.projectid}&projectName=${encodeURIComponent(label)}`)
                    }
                    className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 text-left shadow-sm transition-colors hover:border-violet-300 hover:bg-violet-50"
                  >
                    <span className="flex items-center gap-3">
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-violet-100 text-violet-600">
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </span>
                      <span>
                        <span className="block text-sm font-medium text-slate-700">{label}</span>
                        <span className="block text-xs text-slate-400">Tap to log time · #{p.projectid}</span>
                      </span>
                    </span>
                    <svg className="h-5 w-5 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {todayUnaccountedMinutes >= 1 && (
          <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="today-unaccounted-link">
            {formatCompactMinutes(todayUnaccountedMinutes)} of your clocked-in time has no visit or
            drive recorded.{' '}
            <Link to="/time" className="font-medium underline">
              Review it on the Time tab
            </Link>
          </div>
        )}

        {/* Manual entry entry-point (#1863).
            Discoverable but unobtrusive — most reps work the scheduled list
            above; manual entry is for edge cases (device failed onsite, live
            takeover of an unassigned task). Lands in the consolidated
            pre-flight which then runs the canonical visit flow. */}
        <div className="mt-6">
          <button
            type="button"
            onClick={() => navigate('/manual-entry/new')}
            className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-white py-3 text-sm font-medium text-slate-600 transition-colors hover:border-primary-400 hover:bg-primary-50 hover:text-primary-700"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Working somewhere unscheduled?
          </button>
          <p className="mt-1 text-center text-xs text-slate-400">
            Check in now at a store you're covering, or record a visit after the fact.
          </p>
        </div>
      </main>

      {/* Clock-in asks where the rep is starting instead of assuming a drive */}
      {clockInFork && (
        <ClockInLocationFork
          candidate={clockInFork.candidate}
          onCheckInThere={(candidate) => {
            setClockInFork(null);
            navigate(
              candidate.projectId != null
                ? `/store/${candidate.legacyStoreId}?projectId=${candidate.projectId}`
                : `/store/${candidate.legacyStoreId}`,
            );
          }}
          onHeadingOut={() => {
            setClockInFork(null);
            setOdometerEntryType('start_day');
            setShowOdometerEntry(true);
          }}
          onClockInNoDrive={async () => {
            setClockInFork(null);
            await clockIn(undefined, { openDrive: false });
          }}
          onClose={() => setClockInFork(null)}
        />
      )}

      {/* Install Prompt */}
      {isInstallable && !isStandalone && (
        <InstallPrompt
          isIOS={isIOS}
          showIOSInstructions={showIOSInstructions}
          onInstall={handleInstall}
          onDismiss={dismissPrompt}
          onCloseIOSInstructions={() => setShowIOSInstructions(false)}
        />
      )}
    </div>
  );
}
